"use client";
import React from "react";
import Image from "next/image";
import { motion } from "framer-motion";

// Material-UI Icons
import Facebook from "@mui/icons-material/Facebook";
import LinkedIn from "@mui/icons-material/LinkedIn";
import Instagram from "@mui/icons-material/Instagram";
import YouTube from "@mui/icons-material/YouTube";
import LocationOn from "@mui/icons-material/LocationOn";
import Phone from "@mui/icons-material/Phone";
import Email from "@mui/icons-material/Email";

// Data Lists
const quickLinks = [
  "Apply Online",
  "Campus Virtual Tour",
  "GRL",
  "Committee Member",
  "Mechanism of GRC",
  "Student Satisfaction Survey",
  "Guest Lecture",
  "Fee Structure 2025",
  "RTI",
  "Ideathon Registration",
];

const otherQuickLinks = [
  "Affiliation & Approvals",
  "Admission Process",
  "Student Clubs",
  "Career Form",
  "General Grievance Redressal Committee",
  "Alumni Association",
  "NIRF",
  "NAAC A++",
  "Academic Calendar",
];

const coursesOffered = [
  "B.Tech CE",
  "B.Tech CSE",
  "B.Tech AIML",
  "B.Tech ++",
  "B.Tech Data Science",
  "B.Tech ME",
  "B.Tech ECE",
  "B.Tech Civil Engineering",
  "MBA",
  "BCA",
  "B. Pharm",
  "B.Sc. (Hons) Agriculture",
  "D. Pharm",
  "BBA",
];

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.6,
      ease: [0, 0, 0.2, 1] as any
    }
  }
};

const hoverVariants = {
  hover: {
    y: -5,
    scale: 1.05,
    transition: {
      duration: 0.3,
      ease: [0.42, 0, 0.58, 1] as any
    }
  }
};

const pulseVariants = {
  initial: { scale: 1 },
  pulse: {
    scale: [1, 1.05, 1],
    transition: {
      duration: 2,
      repeat: Infinity,
      ease: [0.42, 0, 0.58, 1] as any
    }
  }
};

// Reusable Link Component with Animation
const FooterLink = ({ text }: { text: string }) => (
  <motion.li
    variants={itemVariants}
    whileHover="hover"
    className="group"
  >
    <motion.a
      href="https://shivalikcollege.edu.in"
      className="text-gray-600 hover:text-blue-600 transition-colors duration-300 group-hover:translate-x-2 transform transition-transform flex items-center"
      whileHover={{ x: 5 }}
    >
      <span className="w-2 h-2 bg-blue-500 rounded-full mr-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
      {text}
    </motion.a>
  </motion.li>
);

const Footer: React.FC = () => {
  return (
    <motion.footer 
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      className="bg-gradient-to-br from-gray-50 to-blue-50 text-gray-700 font-sans border-t border-blue-200 relative overflow-hidden"
    >
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div 
          animate={{
            x: [0, 100, 0],
            y: [0, -50, 0],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: [0, 0, 1, 1] as any
          }}
          className="absolute -top-20 -right-20 w-40 h-40 bg-blue-200 rounded-full opacity-20 blur-xl"
        />
        <motion.div 
          animate={{
            x: [0, -80, 0],
            y: [0, 60, 0],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: [0, 0, 1, 1] as any
          }}
          className="absolute -bottom-20 -left-20 w-60 h-60 bg-orange-200 rounded-full opacity-20 blur-xl"
        />
      </div>

      <div className="container mx-auto px-6 py-12 relative z-10">
        {/* TOP SECTION: LINKS AND CONTACT */}
        <motion.div 
          variants={containerVariants}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-16"
        >
          {/* Quick Links */}
          <motion.div variants={itemVariants}>
            <motion.h3 
              className="font-bold text-gray-900 uppercase mb-6 text-lg relative inline-block"
              whileHover={{ scale: 1.05 }}
            >
              Quick Links
              <motion.div 
                className="absolute bottom-0 left-0 w-1/2 h-1 bg-blue-500 rounded-full"
                whileHover={{ width: "100%" }}
                transition={{ duration: 0.3 }}
              />
            </motion.h3>
            <motion.ul 
              variants={containerVariants}
              className="space-y-4"
            >
              {quickLinks.map((link) => (
                <FooterLink key={link} text={link} />
              ))}
            </motion.ul>
          </motion.div>

          {/* Other Quick Links */}
          <motion.div variants={itemVariants}>
            <motion.h3 
              className="font-bold text-gray-900 uppercase mb-6 text-lg relative inline-block"
              whileHover={{ scale: 1.05 }}
            >
              Other Quick Links
              <motion.div 
                className="absolute bottom-0 left-0 w-1/2 h-1 bg-green-500 rounded-full"
                whileHover={{ width: "100%" }}
                transition={{ duration: 0.3 }}
              />
            </motion.h3>
            <motion.ul 
              variants={containerVariants}
              className="space-y-4"
            >
              {otherQuickLinks.map((link) => (
                <FooterLink key={link} text={link} />
              ))}
            </motion.ul>
          </motion.div>

          {/* Courses Offered */}
          <motion.div variants={itemVariants}>
            <motion.h3 
              className="font-bold text-gray-900 uppercase mb-6 text-lg relative inline-block"
              whileHover={{ scale: 1.05 }}
            >
              Courses Offered
              <motion.div 
                className="absolute bottom-0 left-0 w-1/2 h-1 bg-purple-500 rounded-full"
                whileHover={{ width: "100%" }}
                transition={{ duration: 0.3 }}
              />
            </motion.h3>
            <motion.ul 
              variants={containerVariants}
              className="space-y-4"
            >
              {coursesOffered.map((link) => (
                <FooterLink key={link} text={link} />
              ))}
            </motion.ul>
          </motion.div>

          {/* Contact and Map */}
          <motion.div variants={itemVariants}>
            <motion.h3 
              className="font-bold text-gray-900 uppercase mb-6 text-lg relative inline-block"
              whileHover={{ scale: 1.05 }}
            >
              Contact Us
              <motion.div 
                className="absolute bottom-0 left-0 w-1/2 h-1 bg-orange-500 rounded-full"
                whileHover={{ width: "100%" }}
                transition={{ duration: 0.3 }}
              />
            </motion.h3>
            
            <motion.div variants={containerVariants} className="space-y-4 mb-6">
              <motion.div 
                variants={itemVariants}
                className="flex items-start space-x-3 group"
                whileHover="hover"
              >
                <motion.div
                  variants={hoverVariants}
                  className="flex-shrink-0"
                >
                  <LocationOn className="text-blue-500 mt-1 group-hover:text-blue-600 transition-colors" />
                </motion.div>
                <p className="text-gray-600 leading-relaxed group-hover:text-gray-800 transition-colors">
                  Shimla Bypass Rd, Shinwala, P.O. Sherpur,<br />
                  Dehradun, Uttarakhand 248197
                </p>
              </motion.div>

              <motion.div 
                variants={itemVariants}
                className="flex items-center space-x-3 group"
                whileHover="hover"
              >
                <motion.div
                  variants={hoverVariants}
                  className="flex-shrink-0"
                >
                  <Phone className="text-green-500 group-hover:text-green-600 transition-colors" />
                </motion.div>
                <a
                  href="tel:+919997155111"
                  className="text-gray-600 hover:text-green-600 transition-colors font-medium"
                >
                  +91 9997155111
                </a>
              </motion.div>

              <motion.div 
                variants={itemVariants}
                className="flex items-center space-x-3 group"
                whileHover="hover"
              >
                <motion.div
                  variants={hoverVariants}
                  className="flex-shrink-0"
                >
                  <Email className="text-red-500 group-hover:text-red-600 transition-colors" />
                </motion.div>
                <a
                  href="mailto:info@shivalikcollege.edu.in"
                  className="text-gray-600 hover:text-red-600 transition-colors font-medium"
                >
                  info@shivalikcollege.edu.in
                </a>
              </motion.div>
            </motion.div>

            {/* Animated Map Section */}
            <motion.div 
              variants={itemVariants}
              className="mb-2"
              whileHover="hover"
            >
              <motion.h4 
                className="font-bold text-gray-900 mb-4 flex items-center"
                variants={hoverVariants}
              >
                <LocationOn className="text-orange-500 mr-2" />
                Visit Campus
              </motion.h4>
              <motion.div
                variants={pulseVariants}
                animate="pulse"
                className="w-full h-48 rounded-xl overflow-hidden border-2 border-orange-400 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <iframe
                  title="Shivalik College Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3454.1005461746915!2d77.86815819999999!3d30.336507899999998!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390f2a7095b0a67b%3A0xc2f54efbde26299!2sShivalik%20College%2C%20Dehradun!5e0!3m2!1sen!2sin!4v1722249592216!5m2!1sen!2sin"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  loading="lazy"
                  allowFullScreen
                  className="hover:scale-105 transition-transform duration-500"
                />
              </motion.div>
              <motion.p 
                className="text-gray-600 italic text-sm mt-2 text-center"
                whileHover={{ scale: 1.05 }}
              >
                Shimla Bypass Road, Dehradun
              </motion.p>
            </motion.div>
          </motion.div>
        </motion.div>

        {/* MIDDLE SECTION: LOGO, MISSION, SOCIALS */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center py-16 my-10 border-t border-b border-blue-200 bg-white/50 backdrop-blur-sm rounded-3xl shadow-lg"
        >
          {/* Two Logos Side-by-Side with Animation */}
          <motion.div 
            className="flex justify-center items-center gap-8 mb-8"
            whileHover="hover"
          >
            <motion.div
              variants={hoverVariants}
              className="relative"
            >
              <Image
                src="/images/logo.png"
                alt="Shivalik College Logo"
                width={140}
                height={140}
                className="object-contain drop-shadow-lg hover:drop-shadow-xl transition-all duration-300"
                priority
              />
            </motion.div>
            <motion.div
              variants={hoverVariants}
              className="relative"
            >
              <Image
                src="/images/naac-a-logo.png"
                alt="NAAC A++ Logo"
                width={120}
                height={120}
                className="object-contain drop-shadow-lg hover:drop-shadow-xl transition-all duration-300"
                priority
              />
            </motion.div>
          </motion.div>

          {/* Mission Statement */}
          <motion.p 
            className="max-w-3xl mx-auto text-gray-600 leading-relaxed mb-8 px-4 text-lg font-light"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, duration: 0.8 }}
          >
            Shivalik College of Engineering strives to be a community of academics,
            students, staff, alumni, and researchers working together in cooperation,
            innovation, and enterprise for a better world.
          </motion.p>

          {/* Social Media Links with Hover Effects */}
          <motion.div 
            className="flex justify-center space-x-8"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5, duration: 0.8 }}
          >
            {[
              { Icon: Facebook, color: "hover:text-blue-600", href: "https://facebook.com/shivalikcollege" },
              { Icon: LinkedIn, color: "hover:text-blue-700", href: "https://linkedin.com/school/shivalikcollege" },
              { Icon: Instagram, color: "hover:text-pink-600", href: "https://instagram.com/shivalikcollege" },
              { Icon: YouTube, color: "hover:text-red-600", href: "https://youtube.com/@shivalikcollege" },
            ].map(({ Icon, color, href }, index) => (
              <motion.a
                key={index}
                href={href}
                aria-label={Icon.name}
                className={`text-gray-500 ${color} transition-colors duration-300 bg-white p-3 rounded-full shadow-lg hover:shadow-xl`}
                whileHover={{ 
                  scale: 1.2, 
                  rotate: 360,
                  transition: { duration: 0.5 }
                }}
                whileTap={{ scale: 0.9 }}
              >
                <Icon fontSize="large" />
              </motion.a>
            ))}
          </motion.div>
        </motion.div>

        {/* BOTTOM SECTION: COPYRIGHT + TWO LOGOS */}
        <motion.div 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="flex flex-col sm:flex-row justify-between items-center text-sm text-gray-600 mt-10 border-t border-blue-200 pt-8"
        >
          {/* Left Section with Two Logos */}
          <motion.div 
            className="flex items-center gap-6 mb-4 sm:mb-0"
            whileHover="hover"
          >
            <motion.div 
              className="flex items-center gap-4"
              variants={hoverVariants}
            >
              <motion.div
                whileHover={{ scale: 1.1, rotate: 5 }}
                transition={{ duration: 0.3 }}
              >
                <Image
                  src="/images/logo.png"
                  alt="Shivalik Logo Small"
                  width={50}
                  height={50}
                  className="object-contain drop-shadow-md"
                  priority
                />
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.1, rotate: -5 }}
                transition={{ duration: 0.3 }}
              >
                <Image
                  src="/images/naac-a-logo.png"
                  alt="NAAC A++ Logo"
                  width={55}
                  height={55}
                  className="object-contain drop-shadow-md"
                  priority
                />
              </motion.div>
            </motion.div>
            <motion.p 
              className="ml-2 font-medium"
              whileHover={{ color: "#1e40af" }}
            >
              © 2025 SHIVALIK COLLEGE OF ENGINEERING — All Rights Reserved
            </motion.p>
          </motion.div>

          {/* Right Section */}
          <motion.div 
            className="flex space-x-6"
            variants={containerVariants}
          >
            {["Terms of Service", "Privacy Policy"].map((item, index) => (
              <motion.a
                key={item}
                href="https://shivalikcollege.edu.in"
                className="text-gray-500 hover:text-blue-600 transition-colors duration-300 font-medium relative"
                variants={itemVariants}
                whileHover={{ 
                  scale: 1.1,
                  transition: { duration: 0.2 }
                }}
              >
                {item}
                <motion.span 
                  className="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-600 rounded-full"
                  whileHover={{ width: "100%" }}
                  transition={{ duration: 0.3 }}
                />
              </motion.a>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </motion.footer>
  );
};

export default Footer;